import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/utils/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Target, TrendingUp, TrendingDown, Minus, Plus, Trash2, 
  RefreshCw, CheckCircle, Clock, AlertCircle, Users, FileText, 
  TestTube, Syringe, Truck, BarChart3, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const METRIC_TYPES = [
  { value: 'patients', label: 'Pacientes Cadastrados', icon: Users, color: '#10B981' },
  { value: 'prescriptions', label: 'Receitas Emitidas', icon: FileText, color: '#3B82F6' },
  { value: 'exams', label: 'Exames Solicitados', icon: TestTube, color: '#8B5CF6' },
  { value: 'vaccinations', label: 'Vacinas Aplicadas', icon: Syringe, color: '#F59E0B' },
  { value: 'deliveries', label: 'Entregas Realizadas', icon: Truck, color: '#EC4899' },
  { value: 'quick_prescriptions', label: 'Receitas Avulsas', icon: FileText, color: '#6366F1' }
];

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const PIE_COLORS = ['#10B981', '#F59E0B', '#EF4444'];

export default function Goals() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [showNewGoalModal, setShowNewGoalModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // New goal form state
  const [newGoal, setNewGoal] = useState({
    name: '',
    description: '',
    metric_type: 'patients',
    target_value: 50,
    period_type: 'monthly',
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear()
  });

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [goalsRes, summaryRes, metricsRes] = await Promise.all([
        axiosInstance.get(`/goals?year=${selectedYear}`),
        axiosInstance.get(`/goals/summary?year=${selectedYear}`),
        axiosInstance.get('/goals/current-month')
      ]);
      setGoals(goalsRes.data);
      setSummary(summaryRes.data);
      setCurrentMetrics(metricsRes.data);
    } catch (error) {
      console.error('Erro ao carregar metas:', error);
      toast.error('Erro ao carregar dados das metas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.name.trim()) {
      toast.error('Informe o nome da meta');
      return;
    }
    
    try {
      await axiosInstance.post('/goals', newGoal);
      toast.success('Meta criada com sucesso!');
      setShowNewGoalModal(false);
      setNewGoal({
        name: '',
        description: '',
        metric_type: 'patients',
        target_value: 50,
        period_type: 'monthly',
        period_month: new Date().getMonth() + 1,
        period_year: new Date().getFullYear()
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao criar meta:', error);
      toast.error('Erro ao criar meta');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!confirm('Tem certeza que deseja excluir esta meta?')) return;
    
    try {
      await axiosInstance.delete(`/goals/${goalId}`);
      toast.success('Meta excluída');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir meta');
    }
  };

  const handleSeedDefaultGoals = async () => {
    try {
      const response = await axiosInstance.post('/goals/seed-default');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar metas padrão');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-amber-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 70) return 'bg-emerald-500';
    if (progress >= 50) return 'bg-amber-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  // Prepare data for pie chart
  const pieChartData = summary ? [
    { name: 'Concluídas', value: summary.completed_goals, fill: PIE_COLORS[0] },
    { name: 'Em Progresso', value: summary.in_progress_goals, fill: PIE_COLORS[1] },
    { name: 'Não Iniciadas', value: summary.not_started_goals, fill: PIE_COLORS[2] }
  ] : [];

  // Prepare data for bar chart
  const barChartData = summary?.by_metric 
    ? Object.entries(summary.by_metric).map(([key, data]) => ({
        name: data.label.split(' ')[0], // First word only
        target: data.target_total,
        current: data.current_total,
        fullLabel: data.label
      }))
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)]">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)]">
      <Navbar />
      
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                <Target className="w-7 h-7 text-primary" />
                Metas e Métricas
              </h1>
              <p className="text-muted-foreground text-sm md:text-base">
                Acompanhe o progresso dos objetivos do sistema
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSeedDefaultGoals} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Metas Padrão
            </Button>
            <Button onClick={() => setShowNewGoalModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nova Meta
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total de Metas</p>
                  <p className="text-2xl font-bold">{summary?.total_goals || 0}</p>
                </div>
                <Target className="w-8 h-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Concluídas</p>
                  <p className="text-2xl font-bold text-green-700">{summary?.completed_goals || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Em Progresso</p>
                  <p className="text-2xl font-bold text-amber-700">{summary?.in_progress_goals || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Progresso Médio</p>
                  <p className="text-2xl font-bold">{summary?.average_progress || 0}%</p>
                </div>
                <BarChart3 className="w-8 h-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Pie Chart - Goals Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status das Metas</CardTitle>
              <CardDescription>Distribuição por status de conclusão</CardDescription>
            </CardHeader>
            <CardContent>
              {pieChartData.length > 0 && pieChartData.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-gray-500">
                  <p>Nenhuma meta cadastrada</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bar Chart - Target vs Current */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Meta vs Realizado</CardTitle>
              <CardDescription>Comparação por tipo de métrica</CardDescription>
            </CardHeader>
            <CardContent>
              {barChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={barChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [
                        value, 
                        name === 'target' ? 'Meta' : 'Realizado'
                      ]}
                      labelFormatter={(label) => barChartData.find(d => d.name === label)?.fullLabel || label}
                    />
                    <Legend formatter={(value) => value === 'target' ? 'Meta' : 'Realizado'} />
                    <Bar dataKey="target" fill="#94A3B8" name="target" />
                    <Bar dataKey="current" fill="#0F766E" name="current" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-gray-500">
                  <p>Nenhum dado disponível</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Current Month Metrics */}
        {currentMetrics && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Métricas de {currentMetrics.month_name} {currentMetrics.year}
              </CardTitle>
              <CardDescription>Comparação com o mês anterior</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {METRIC_TYPES.map((metric) => {
                  const data = currentMetrics.metrics[metric.value];
                  if (!data) return null;
                  const Icon = metric.icon;
                  return (
                    <div 
                      key={metric.value} 
                      className="p-3 rounded-lg border bg-white"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Icon className="w-5 h-5" style={{ color: metric.color }} />
                        {getTrendIcon(data.trend_direction)}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{data.label}</p>
                      <p className="text-xl font-bold">{data.current_month}</p>
                      <p className="text-xs text-muted-foreground">
                        Anterior: {data.previous_month}
                        <span className={`ml-1 ${data.trend_percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ({data.trend_percentage >= 0 ? '+' : ''}{data.trend_percentage}%)
                        </span>
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Goals List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Metas Cadastradas</CardTitle>
            <CardDescription>Lista de todas as metas de {selectedYear}</CardDescription>
          </CardHeader>
          <CardContent>
            {goals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhuma meta cadastrada para {selectedYear}</p>
                <Button onClick={() => setShowNewGoalModal(true)} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeira Meta
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {goals.map((goal) => {
                  const metricConfig = METRIC_TYPES.find(m => m.value === goal.metric_type);
                  const Icon = metricConfig?.icon || Target;
                  
                  return (
                    <div 
                      key={goal.id} 
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${metricConfig?.color}20` }}
                          >
                            <Icon className="w-5 h-5" style={{ color: metricConfig?.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold truncate">{goal.name}</h3>
                              {getStatusIcon(goal.status)}
                              <Badge variant="outline" className="text-xs">
                                {goal.period_type === 'monthly' 
                                  ? MONTHS[(goal.period_month || 1) - 1] 
                                  : 'Anual'} {goal.period_year}
                              </Badge>
                            </div>
                            {goal.description && (
                              <p className="text-sm text-muted-foreground truncate">{goal.description}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex-1 md:max-w-[300px]">
                          <div className="flex justify-between text-sm mb-1">
                            <span>{goal.current_value} / {goal.target_value}</span>
                            <span className="font-semibold">{goal.progress_percentage}%</span>
                          </div>
                          <Progress 
                            value={Math.min(goal.progress_percentage, 100)} 
                            className="h-2"
                          />
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteGoal(goal.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Goal Modal */}
      <Dialog open={showNewGoalModal} onOpenChange={setShowNewGoalModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nova Meta</DialogTitle>
            <DialogDescription>
              Defina uma nova meta para acompanhar o progresso
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome da Meta</Label>
              <Input
                value={newGoal.name}
                onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                placeholder="Ex: Cadastrar 100 pacientes"
              />
            </div>
            
            <div>
              <Label>Descrição (opcional)</Label>
              <Input
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                placeholder="Descrição da meta"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Métrica</Label>
                <Select 
                  value={newGoal.metric_type} 
                  onValueChange={(v) => setNewGoal({ ...newGoal, metric_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METRIC_TYPES.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Valor da Meta</Label>
                <Input
                  type="number"
                  min="1"
                  value={newGoal.target_value}
                  onChange={(e) => setNewGoal({ ...newGoal, target_value: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Período</Label>
                <Select 
                  value={newGoal.period_type} 
                  onValueChange={(v) => setNewGoal({ ...newGoal, period_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensal</SelectItem>
                    <SelectItem value="yearly">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Ano</Label>
                <Select 
                  value={newGoal.period_year.toString()} 
                  onValueChange={(v) => setNewGoal({ ...newGoal, period_year: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {newGoal.period_type === 'monthly' && (
              <div>
                <Label>Mês</Label>
                <Select 
                  value={newGoal.period_month.toString()} 
                  onValueChange={(v) => setNewGoal({ ...newGoal, period_month: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month, index) => (
                      <SelectItem key={index} value={(index + 1).toString()}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewGoalModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateGoal}>
              Criar Meta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
